import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import axios from "axios";
import { Box, Button, Typography } from "@mui/material";
import { ReactComponent as MicrophoneIcon } from "../assets/microphone.svg";

const VoiceRecorder = forwardRef(
  ({ doneRecording, assessmentComplete, stacked = false, hideUi = false }, ref) => {
    const [recording, setRecording] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const gainNodeRef = useRef(null);
    const silenceTimerRef = useRef(null);
    const silenceIntervalRef = useRef(null); // new ref for interval
    const [fileName, setFileName] = useState("Test");
    const [fileToUpload, setFileToUpload] = useState(null);

    const streamRef = useRef(null);
    const sourceRef = useRef(null);

    useImperativeHandle(ref, () => ({
      startRecording,
      stopRecording,
      uploadFileToS3,
    }));

    const startRecording = async (assessmentId) => {
      setFileName(`${assessmentId}.webm`);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream; // keep a handle so we can stop tracks later

      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      sourceRef.current =
        audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      sourceRef.current.connect(analyserRef.current);
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.gain.value = 0;
      analyserRef.current.connect(gainNodeRef.current);
      gainNodeRef.current.connect(audioContextRef.current.destination);

      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = []; // reset
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.start();
      setRecording(true);
      monitorSilence();
    };


    // Optionally, you can also start monitoring in a useEffect:
    // useEffect(() => {
    //   if (recording) {
    //     monitorSilence();
    //   }
    // }, [recording]);

    const monitorSilence = () => {
      const analyser = analyserRef.current;
      if (!analyser) return;
      const dataArray = new Uint8Array(analyser.fftSize);

      const checkSilence = () => {
        analyser.getByteTimeDomainData(dataArray);
        const sum = dataArray.reduce(
          (acc, value) => acc + (value - 128) ** 2,
          0
        );
        const average = Math.sqrt(sum / dataArray.length) / 128;

        // If volume is below threshold, start the silence timer
        if (average < 0.01) {
          if (silenceTimerRef.current === null) {
            silenceTimerRef.current = setTimeout(() => {
              stopRecording();
            }, 4000);
          }
        } else {
          // If noise is detected, clear the timer
          if (silenceTimerRef.current !== null) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
        }
      };

      // Check silence every 100ms
      silenceIntervalRef.current = setInterval(checkSilence, 100);
    };

    const stopRecording = () => {
      // stop silence checks immediately
      if (silenceIntervalRef.current) {
        clearInterval(silenceIntervalRef.current);
        silenceIntervalRef.current = null;
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }

      if (!mediaRecorderRef.current) return;

      mediaRecorderRef.current.onstop = async () => {
        // build the blob
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        audioChunksRef.current = []; // release chunk refs ASAP

        // create URL for preview (if you need it)
        const url = URL.createObjectURL(blob);
        setAudioUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev); // revoke any previous URL
          return url;
        });

        // file for upload
        const file = new File([blob], fileName, { type: "audio/webm" });
        setFileToUpload(file);

        // release blob reference (the File still holds data)
        // eslint-disable-next-line no-unused-vars
        // blob = null; // (optional in JS; GC will collect once unreferenced)

        await cleanupMedia();
        setRecording(false);
        doneRecording();
      };

      // ensure final dataavailable fires on some browsers
      try {
        mediaRecorderRef.current.requestData?.();
      } catch {}
      mediaRecorderRef.current.stop();
    };


    const uploadFileToS3 = async (presignedUrl) => {
      try {
        const response = await axios.put(presignedUrl, fileToUpload, {
          headers: { "Content-Type": "audio/webm" },
        });

        if (response.status === 200) {
          // release memory now that server has it
          if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
            setAudioUrl(null);
          }
          setFileToUpload(null);

          assessmentComplete();
        } else {
          console.error("File upload failed:", response.data);
        }
      } catch (error) {
        console.error(
          "Error uploading file:",
          error.response ? error.response.data : error.message
        );
      }
    };


    const cleanupMedia = async () => {
      // stop mic tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      // disconnect audio nodes
      try {
        sourceRef.current?.disconnect();
      } catch {}
      try {
        analyserRef.current?.disconnect();
      } catch {}
      try {
        gainNodeRef.current?.disconnect();
      } catch {}
      sourceRef.current = null;
      analyserRef.current = null;
      gainNodeRef.current = null;

      // close audio context
      if (audioContextRef.current) {
        try {
          await audioContextRef.current.close();
        } catch {}
        audioContextRef.current = null;
      }

      // clear recorder + handlers
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current = null;
      }

      // clear timers
      if (silenceIntervalRef.current) {
        clearInterval(silenceIntervalRef.current);
        silenceIntervalRef.current = null;
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    };


    if (!recording || hideUi) {
      return null;
    }

    return (
      <Box
        sx={{
          position: stacked ? "relative" : "absolute",
          bottom: stacked
            ? "auto"
            : "calc(env(safe-area-inset-bottom, 0px) + var(--panel-horizontal-offset, var(--panel-left-offset, 20px)))",
          right: stacked
            ? "auto"
            : "calc(env(safe-area-inset-right, 0px) + var(--panel-horizontal-offset, var(--panel-left-offset, 20px)))",
          width: stacked ? "100%" : "var(--panel-width-standard, 250px)",
          maxWidth: stacked
            ? "100%"
            : "calc(100vw - var(--panel-horizontal-offset, var(--panel-left-offset, 20px)) - var(--panel-horizontal-offset, var(--panel-left-offset, 20px)))",
          backgroundColor: "var(--surface-bg, rgba(9, 12, 18, 0.62))",
          boxShadow: "var(--surface-shadow, 0 14px 34px rgba(0, 0, 0, 0.34))",
          borderRadius: "var(--radius-lg, 14px)",
          backdropFilter:
            "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
          WebkitBackdropFilter:
            "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
          px: 2,
          py: 1.75,
          boxSizing: "border-box",
          zIndex: stacked ? "auto" : 1000,
          color: "#fff",
          pointerEvents: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            mb: 1.25,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 52,
              height: 52,
              flexShrink: 0,
              "@keyframes voicePulse": {
                "0%": { transform: "scale(0.92)", opacity: 0.55 },
                "65%": { transform: "scale(1.25)", opacity: 0 },
                "100%": { transform: "scale(1.25)", opacity: 0 },
              },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: -7,
                borderRadius: "50%",
                background: "var(--recording-pulse-bg, rgba(124, 253, 255, 0.24))",
                animation: "voicePulse 1.7s ease-out infinite",
              }}
            />
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: "var(--recording-mic-gradient)",
                boxShadow: "var(--recording-mic-shadow, 0 8px 20px rgba(60, 201, 194, 0.34))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MicrophoneIcon width={24} height={24} />
            </Box>
          </Box>

          <Box sx={{ textAlign: "left", minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                opacity: 0.74,
                letterSpacing: "0.11em",
                lineHeight: 1.1,
              }}
            >
              Voice Capture
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontSize: "1.05rem", lineHeight: 1.2, fontWeight: 650 }}
            >
              Recording...
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={stopRecording}
          disabled={!recording}
          fullWidth
          sx={{
            mt: 0.5,
            borderRadius: "var(--radius-pill, 999px)",
            py: 0.95,
            textTransform: "none",
            fontWeight: 700,
            letterSpacing: "0.02em",
            background: "var(--recording-cta-gradient)",
            color: "var(--recording-cta-text, #083033)",
            boxShadow: "var(--recording-cta-shadow, 0 10px 24px rgba(77, 196, 187, 0.35))",
            "&:hover": {
              background: "var(--recording-cta-gradient-hover)",
            },
            "&.Mui-disabled": {
              background: "var(--recording-cta-disabled, rgba(133, 245, 236, 0.35))",
              color: "var(--recording-cta-text-disabled, rgba(6, 25, 27, 0.5))",
            },
          }}
        >
          I&apos;m done
        </Button>
      </Box>
    );
  }
);

export default VoiceRecorder;
